import { z } from 'zod';

import type { QuizQuestion } from './logic';

export const QUIZ_QUESTION_TYPES = [
  'multipleChoice',
  'openEnded',
  'fillInTheBlank',
  'trueOrFalse',
  'multiSelect',
  'oddOneOut',
  'tapPairs',
  'categorize',
  'sequence',
] as const;
export type QuizQuestionType = (typeof QUIZ_QUESTION_TYPES)[number];

/**
 * The kinds a fresh quiz setup opens with, on every client: every kind except
 * the two that make the learner TYPE (open-ended, fill-in-the-blank) — those
 * are opt-in. Product call (QA board, 2026-10-09).
 */
const TYPED_KINDS: readonly QuizQuestionType[] = ['openEnded', 'fillInTheBlank'];
export const DEFAULT_QUIZ_QUESTION_TYPES: readonly QuizQuestionType[] =
  QUIZ_QUESTION_TYPES.filter((kind) => !TYPED_KINDS.includes(kind));
export const quizQuestionKindSchema = z.enum(QUIZ_QUESTION_TYPES);

/**
 * Fields every kind shares. DECLARATION ORDER IS THE EMISSION ORDER: Gemini
 * writes the properties in the order the response schema lists them (verified
 * with the eval on 3.8 Flash), so `kind` comes first — the client learns the
 * kind before the text streams.
 */
const head = {
  /** Question text. Fill-in-the-blank carries exactly one `<blank />`. */
  prompt: z.string(),
};
/** Batch-local indices of the source cards / chunks used. */
const sourceIds = z.array(z.number().int());
const nonEmpty = z.string().trim().min(1);
const pair = z.object({ left: z.string(), right: z.string() });
const bucket = z.object({ name: z.string(), items: z.array(z.string()) });

/**
 * Options compare as the boards tell tiles apart: by their exact text, trimmed.
 * Case is meaning, not noise ("Co" and "CO", "mW" and "MW" are different answers).
 */
const sameText = (s: string) => s.trim();
/** `xs` without repeats and without anything in `taken`, first spelling kept. */
function distinctFrom(xs: readonly string[], taken: readonly string[] = []): string[] {
  const seen = new Set(taken.map(sameText));
  return xs.filter((x) => {
    const key = sameText(x);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
const allDistinct = (xs: readonly string[]) => distinctFrom(xs).length === xs.length;

/**
 * A fixed-size list the model MUST fill, as `<name>1 … <name>N` required string
 * fields. `minItems`/`maxItems` never reach Gemini: `@ai-sdk/google` drops them
 * (and tuple `items`) on the way to `responseSchema`, so an array of three is only
 * a request. A required key is what constrained decoding enforces, so the model
 * cannot stop at two (measured: whole batches of two-distractor multiple choice).
 */
function numberedFields<N extends string>(name: N, count: number) {
  return Object.fromEntries(
    Array.from({ length: count }, (_, i) => [`${name}${i + 1}`, z.string()]),
  ) as Record<`${N}${number}`, z.ZodString>;
}
/**
 * The client's view of `numberedFields`: the numbered values as `list`, in order,
 * skipping a missing one so the list rule decides. An element that already carries
 * `list` as an array (a fixture, an older stream) is left alone.
 */
function gatherNumbered(el: unknown, name: string, list: string): unknown {
  if (typeof el !== 'object' || el === null || Array.isArray(el)) return el;
  const record = el as Record<string, unknown>;
  if (Array.isArray(record[list])) return el;
  const values: unknown[] = [];
  for (let i = 1; `${name}${i}` in record; i++) values.push(record[`${name}${i}`]);
  return { ...record, [list]: values };
}
/** Wrong options a multiple choice carries, and group items an odd one out does. */
const CHOICE_DISTRACTORS = 3;
/** Same token the web and mobile renderers split the prompt on. */
const BLANK = /<\s*blank\s*\/?\s*>/gi;

/**
 * Size ranges per kind. Minimums and the tapPairs / sequence / multiSelect maxima
 * hold on every question a client accepts; the categorize maxima are only prompt
 * targets (cutting a bucket or an item would change what the question tests).
 */
export const QUIZ_KIND_LIMITS = {
  tapPairs: { min: 3, max: 7 },
  categorize: { buckets: { min: 2, max: 4 }, items: { min: 4, max: 8 } },
  sequence: { min: 3, max: 6 },
  multiSelect: { options: { min: 4, max: 8 } },
} as const;

type Element<S extends z.ZodRawShape> = {
  prompt: string;
  sourceIds: number[];
} & z.infer<z.ZodObject<S>>;
type StructuredOutputOption = z.ZodDiscriminatedUnionOption<'kind'>;

function defineQuestionKind<
  K extends QuizQuestionType,
  W extends z.ZodRawShape,
  S extends z.ZodRawShape,
>(
  kind: K,
  def: {
    /**
     * Answer fields as the server streams them: only what Gemini's constrained
     * decoding actually enforces (types, required keys). Anything stricter is
     * dangerous — when an element fails the server-side schema the SDK stops
     * publishing, drops that element AND every later one, and flushes the raw
     * model text into the stream (seen in production with `sourceIds.max(3)`).
     */
    structuredOutputSchema: W;
    /** Answer fields as the CLIENT accepts a playable question. */
    storedSchema: S;
    /** Reshapes the streamed element into `storedSchema`'s shape before it is checked. */
    normalize?: (el: unknown) => unknown;
    /**
     * Cross-field rules the schema can't express. Returns the element with
     * harmless repeats removed, or null when the question contradicts itself
     * (a repeated step, an item in two buckets) and no board could play it.
     */
    repair?: (el: Element<S>) => Element<S> | null;
    /** Maps a validated element onto the renderer's `QuizQuestion`. */
    toQuestion: (el: Element<S>) => QuizQuestion;
  },
) {
  const structuredOutputSchema: StructuredOutputOption = z.object({
    kind: z.literal(kind),
    ...head,
    ...def.structuredOutputSchema,
    sourceIds,
  });
  const storedSchema = z.object({
    kind: z.literal(kind),
    prompt: nonEmpty,
    ...def.storedSchema,
    sourceIds,
  });
  return {
    structuredOutputSchema,
    /** A streamed element as a playable question, or null when it fails the client rules. */
    parse: (el: unknown): QuizQuestion | null => {
      const parsed = storedSchema.safeParse(def.normalize ? def.normalize(el) : el);
      if (!parsed.success) return null;
      const element = parsed.data as Element<S>;
      const repaired = def.repair ? def.repair(element) : element;
      return repaired ? def.toQuestion(repaired) : null;
    },
  };
}

/**
 * The one table of quiz question kinds: schemas and renderer mapping per kind. Prompt text lives in
 * `flashka-web/server/routes/ai/quiz-kind-prompts.ts`, keyed by
 * `QuizQuestionType`, so a kind added here fails typecheck until it follows.
 */
export const QUESTION_KINDS = {
  multipleChoice: defineQuestionKind('multipleChoice', {
    /** Four options: the solution and `wrongOption1`–`wrongOption3`, all required. */
    structuredOutputSchema: {
      solution: z.string(),
      ...numberedFields('wrongOption', CHOICE_DISTRACTORS),
    },
    storedSchema: {
      solution: nonEmpty,
      wrongOptions: z.array(nonEmpty).min(CHOICE_DISTRACTORS),
    },
    normalize: (el) => gatherNumbered(el, 'wrongOption', 'wrongOptions'),
    // A wrong option that repeats the answer or another wrong option is dropped.
    repair: (el) => {
      const wrongOptions = distinctFrom(el.wrongOptions, [el.solution]);
      return wrongOptions.length >= CHOICE_DISTRACTORS ? { ...el, wrongOptions } : null;
    },
    toQuestion: (el) => ({
      questionType: 'multipleChoice',
      question: el.prompt,
      correctAnswer: el.solution,
      // The renderer has four slots: solution + three distractors.
      distractors: el.wrongOptions.slice(0, CHOICE_DISTRACTORS),
      sources: el.sourceIds,
    }),
  }),
  openEnded: defineQuestionKind('openEnded', {
    /** Sample answer the grader compares against (≤ 120 words). */
    structuredOutputSchema: { solution: z.string() },
    storedSchema: { solution: nonEmpty },
    toQuestion: (el) => ({
      questionType: 'openEnded',
      question: el.prompt,
      correctAnswer: el.solution,
      sources: el.sourceIds,
    }),
  }),
  fillInTheBlank: defineQuestionKind('fillInTheBlank', {
    /** The blanked words, verbatim. */
    structuredOutputSchema: { solution: z.string() },
    storedSchema: { solution: nonEmpty },
    repair: (el) => ((el.prompt.match(BLANK) ?? []).length === 1 ? el : null),
    toQuestion: (el) => ({
      questionType: 'fillInTheBlank',
      question: el.prompt,
      correctAnswer: el.solution,
      sources: el.sourceIds,
    }),
  }),
  trueOrFalse: defineQuestionKind('trueOrFalse', {
    structuredOutputSchema: { solution: z.boolean() },
    storedSchema: { solution: z.boolean() },
    toQuestion: (el) => ({
      questionType: 'trueOrFalse',
      question: el.prompt,
      correctAnswer: el.solution,
      sources: el.sourceIds,
    }),
  }),
  multiSelect: defineQuestionKind('multiSelect', {
    structuredOutputSchema: {
      solutions: z.array(z.string()),
      wrongOptions: z.array(z.string()),
    },
    storedSchema: {
      solutions: z.array(nonEmpty).min(2),
      wrongOptions: z.array(nonEmpty).min(1),
    },
    // Repeats are dropped; an option that is both right and wrong is a contradiction.
    repair: (el) => {
      const solutions = distinctFrom(el.solutions);
      const wrongOptions = distinctFrom(el.wrongOptions);
      if (distinctFrom(wrongOptions, solutions).length < wrongOptions.length) return null;
      return solutions.length >= 2 &&
        wrongOptions.length >= 1 &&
        solutions.length + wrongOptions.length >= QUIZ_KIND_LIMITS.multiSelect.options.min
        ? { ...el, solutions, wrongOptions }
        : null;
    },
    toQuestion: (el) => {
      const { max } = QUIZ_KIND_LIMITS.multiSelect.options;
      return {
        questionType: 'multiSelect',
        question: el.prompt,
        correctAnswer: el.solutions.slice(0, max - 1),
        distractors: el.wrongOptions.slice(
          0,
          max - Math.min(el.solutions.length, max - 1),
        ),
        sources: el.sourceIds,
      };
    },
  }),
  oddOneOut: defineQuestionKind('oddOneOut', {
    /** `solution` is the item that does not belong; `groupItem1`–`3` the three that do. */
    structuredOutputSchema: {
      solution: z.string(),
      ...numberedFields('groupItem', CHOICE_DISTRACTORS),
    },
    storedSchema: {
      solution: nonEmpty,
      group: z.array(nonEmpty).min(CHOICE_DISTRACTORS),
    },
    normalize: (el) => gatherNumbered(el, 'groupItem', 'group'),
    repair: (el) => {
      const group = distinctFrom(el.group, [el.solution]);
      return group.length >= CHOICE_DISTRACTORS ? { ...el, group } : null;
    },
    toQuestion: (el) => ({
      questionType: 'oddOneOut',
      question: el.prompt,
      correctAnswer: el.solution,
      distractors: el.group.slice(0, CHOICE_DISTRACTORS),
      sources: el.sourceIds,
    }),
  }),
  tapPairs: defineQuestionKind('tapPairs', {
    structuredOutputSchema: { pairs: z.array(pair) },
    storedSchema: {
      pairs: z
        .array(z.object({ left: nonEmpty, right: nonEmpty }))
        .min(QUIZ_KIND_LIMITS.tapPairs.min),
    },
    toQuestion: (el) => ({
      questionType: 'tapPairs',
      question: el.prompt,
      pairs: el.pairs.slice(0, QUIZ_KIND_LIMITS.tapPairs.max),
      sources: el.sourceIds,
    }),
  }),
  categorize: defineQuestionKind('categorize', {
    structuredOutputSchema: { buckets: z.array(bucket) },
    storedSchema: {
      buckets: z
        .array(z.object({ name: nonEmpty, items: z.array(nonEmpty).min(1) }))
        .min(QUIZ_KIND_LIMITS.categorize.buckets.min)
        .refine(
          (b) =>
            b.reduce((sum, x) => sum + x.items.length, 0) >=
            QUIZ_KIND_LIMITS.categorize.items.min,
        ),
    },
    // A repeat inside one bucket is dropped; an item under two buckets, or two
    // buckets with one name, can never be sorted right.
    repair: (el) => {
      const buckets = el.buckets.map((b) => ({ ...b, items: distinctFrom(b.items) }));
      if (!allDistinct(buckets.map((b) => b.name))) return null;
      const items = buckets.flatMap((b) => b.items);
      if (!allDistinct(items)) return null;
      return items.length >= QUIZ_KIND_LIMITS.categorize.items.min
        ? { ...el, buckets }
        : null;
    },
    toQuestion: (el) => ({
      questionType: 'categorize',
      question: el.prompt,
      buckets: el.buckets,
      sources: el.sourceIds,
    }),
  }),
  sequence: defineQuestionKind('sequence', {
    /** The steps in the correct order. */
    structuredOutputSchema: { steps: z.array(z.string()) },
    storedSchema: {
      steps: z.array(nonEmpty).min(QUIZ_KIND_LIMITS.sequence.min),
    },
    // A repeated step has no single right place.
    repair: (el) => (allDistinct(el.steps) ? el : null),
    toQuestion: (el) => ({
      questionType: 'sequence',
      question: el.prompt,
      // Dropping the tail keeps the order right; the prompt asks for at most `max`.
      steps: el.steps.slice(0, QUIZ_KIND_LIMITS.sequence.max),
      sources: el.sourceIds,
    }),
  }),
} satisfies Record<QuizQuestionType, unknown>;

const quizQuestionStructuredOutputSchema = z.discriminatedUnion(
  'kind',
  QUIZ_QUESTION_TYPES.map((k) => QUESTION_KINDS[k].structuredOutputSchema) as [
    StructuredOutputOption,
    ...StructuredOutputOption[],
  ],
);

/**
 * The permissive shape the server streams against. `plan` comes first, so
 * the model commits to which facts and kinds it will test before it writes;
 * the client reads the `questions` array as it streams.
 */
export const quizBatchStructuredOutputSchema = z.object({
  plan: z.string(),
  questions: z.array(quizQuestionStructuredOutputSchema),
});

/** What the planned writer streams: the classifier already wrote the plan. */
export const quizPlannedBatchStructuredOutputSchema = z.object({
  questions: z.array(quizQuestionStructuredOutputSchema),
});

/** One streamed element validated and mapped for the renderer; null when unplayable. */
export function parseQuestionElement(el: unknown): QuizQuestion | null {
  const kind = z.object({ kind: quizQuestionKindSchema }).safeParse(el);
  return kind.success ? QUESTION_KINDS[kind.data.kind].parse(el) : null;
}
